import { getCorners } from 'utils/geometry';

export interface CanvasObjectInterface<O> {
	getType(): string;

	setType(type: string): void;

	getScale(): number;

	setScale(scale: number): void;

	getMinDimension(): number;

	getXY(): number[];

	setXY(x: number, y: number): void;

	getOptions(): O;

	setOptions(options: Partial<O>): void;

	getWidthHeight(): number[];

	setWidthHeight(width: number, height: number): void;

	getCorners(): Record<
		| 'topLeftCorner'
		| 'topRightCorner'
		| 'bottomLeftCorner'
		| 'bottomRightCorner',
		{ x: number; y: number }
	>;

	move(movementX: number, movementY: number): void;

	resize(movementX: number, movementY: number): void;

	isPointInside(pointX: number, pointY: number): boolean;
}

export class BaseCanvasObject<O extends BaseDrawOptions>
	implements CanvasObjectInterface<O>
{
	private type = 'base';
	private options: O;
	private minDimension: number;
	private lastOperationType = '';

	constructor(options: O, minDimension = 150) {
		const alignedWidth = options.w * options.scale;
		const alignedHeight = options.h * options.scale;
		this.options = {
			...options,
			w: alignedWidth,
			h: alignedHeight,
			initialWidth: options.initialWidth || options.w,
			initialHeight: options.initialHeight || options.h,
		};
		this.minDimension = minDimension;
	}

	setLastOperationType(type: string) {
		this.lastOperationType = type;
	}

	getLastOperationType(): string {
		const lastOperation = this.lastOperationType;
		this.setLastOperationType('');
		return lastOperation;
	}

	getOptions(): O {
		return { ...this.options, operation: this.getLastOperationType() };
	}

	setOptions(options: Partial<O>) {
		this.options = { ...this.options, ...options };
	}

	getType(): string {
		return this.type;
	}

	setType(type: string) {
		this.type = type;
	}

	getCorners() {
		const { x, y, w, h } = this.getOptions();

		return getCorners(x, y, w, h);
	}

	isPointInside(pointX: number, pointY: number, padding = 0) {
		const { x, y, w, h } = this.getOptions();

		return (
			pointX > x - padding &&
			pointX < x + w + padding &&
			pointY > y - padding &&
			pointY < y + h + padding
		);
	}

	getMinDimension() {
		return this.minDimension;
	}

	getScale(): number {
		return this.options.scale;
	}

	setScale(scale: number) {
		this.options.scale = scale;
	}

	getXY() {
		return [this.options.x, this.options.y];
	}

	setXY(x: number, y: number) {
		this.options.x = x;
		this.options.y = y;
	}

	getWidthHeight() {
		return [this.options.w, this.options.h];
	}

	setWidthHeight(width: number, height: number) {
		if (width >= this.minDimension && height >= this.minDimension) {
			const newValue = Math.min(width, height);
			const prevValue = Math.min(
				this.options?.initialWidth || width,
				this.options?.initialHeight || height
			);
			let scale: number;

			scale = newValue / prevValue;

			this.options.w = width;
			this.options.h = height;
			this.options.scale = scale;
		}
	}

	move(movementX: number, movementY: number) {
		const { x, y } = this.options;
		const layerX = x + movementX;
		const layerY = y + movementY;

		this.setXY(layerX, layerY);
		this.setLastOperationType('moving');
	}

	resize(movementX: number, movementY: number) {
		const { w, h } = this.options;
		const increase = (movementX + movementY) / 2;
		const layerW = w + increase;
		const layerH = h + increase;

		if (layerW >= this.minDimension && layerH >= this.minDimension) {
			this.setWidthHeight(layerW, layerH);
			this.setLastOperationType('resizing');
		}
	}
}
